import { useEffect, useMemo, useState } from 'react';
import { useChatContext, Avatar } from 'stream-chat-react';
import { useSelector } from 'react-redux';
import { ListItem, ListItemAvatar, ListItemText } from '@mui/material';

import type { UserResponse } from 'stream-chat';

import type { StreamChatType } from '../../types';
import { useAdminPanelFormState } from './context/AdminPanelFormContext';
import { ValidationError } from './ValidationError';

const ListContainer = ({ children }: { children: React.ReactNode }) => {
  const { errors, createChannelType } = useAdminPanelFormState();
  const showHeading = !createChannelType || createChannelType === 'team';

  return (
    <div className='user-list__container'>
      {showHeading && (
        <h2>
          <span>Add Members</span>
          <ValidationError errorMessage={errors.members} />
        </h2>
      )}
      <div className='user-list__header user-list__row'>
        <div className='user-list__column-block'>
          <p>User</p>
        </div>
        <div className='user-list__column--checkbox'>
          <p>Invite</p>
        </div>
      </div>
      {children}
    </div>
  );
};

type UserItemProps = {
  index: number;
  user: UserResponse<StreamChatType>;
};

const MOCKED_LAST_ACTIVE_STRINGS = [
  '12 min ago',
  '27 min ago',
  '6 hours ago',
  '14 hours ago',
];

const UserItem = ({ index, user }: UserItemProps) => {
  const { handleMemberSelect } = useAdminPanelFormState();

  const lastActive = MOCKED_LAST_ACTIVE_STRINGS[index] || 'Yesterday';
  const title = user.name || user.id;

  return (
    <label htmlFor={user.id} title={title} className='user-list__row'>
      <div className='user-list__column-block'>
        <ListItem dense sx={{ paddingLeft: 0, paddingRight: 0 }}>
          <ListItemAvatar>
            <Avatar image={user.image} name={title} size={40} />
          </ListItemAvatar>
          <ListItemText
            primary={title}
            secondary={`Last seen ${lastActive}`}
            secondaryTypographyProps={{ fontSize: '11px !important' }}
          />
        </ListItem>
      </div>
      <div className='user-list__column--checkbox'>
        <input
          type='checkbox'
          name='members'
          id={user.id}
          value={user.id}
          onChange={handleMemberSelect}
        />
      </div>
    </label>
  );
};

type UserListLoadState = 'loading' | 'error' | 'empty';

const LOAD_STATE_NOTIFICATION: Record<UserListLoadState, string> = {
  empty: 'No users found.',
  error: 'Error loading, please refresh and try again.',
  loading: 'Loading users...',
};

export const UserList = () => {
  const selectedBarn = useSelector((state: any) => state.barn.selectedBarn);
  const { client, channel } = useChatContext<StreamChatType>();
  const { createChannelType } = useAdminPanelFormState();
  const [loadState, setLoadState] = useState<UserListLoadState | null>(null);
  const [users, setUsers] = useState<UserResponse<StreamChatType>[] | undefined>();

  const channelMembers = useMemo(
    () => (channel?.state?.members
      ? Object.keys(channel.state.members)
      : client.userID
        ? [client.userID]
        : []),
    [channel?.state?.members, client.userID],
  );

  useEffect(() => {
    const getUsers = async () => {
      if (loadState) return;
      setLoadState('loading');

      try {
        const response = await client.queryUsers(
          {
            id: { $nin: channelMembers },
            teams: { $contains: selectedBarn.tenant },
          },
          { id: 1 },
          {},
        );

        if (response.users.length) {
          setUsers(response.users);
        } else {
          setLoadState('empty');
        }
      } catch (event) {
        setLoadState('error');
      }
      setLoadState(null);
    };

    if (client) {
      getUsers();
    }
  }, [client, channelMembers, createChannelType,]);

  return (
    <ListContainer>
      {loadState
        ? <div className='user-list__message'>{LOAD_STATE_NOTIFICATION[loadState]}</div>
        : users && users.length > 0
          ? users.map((user, index) => (
            <UserItem index={index} key={user.id} user={user} />
          ))
          : null}
    </ListContainer>
  );
};
